import {useEffect} from 'react'

/**
 * Safety cleanup for Chakra v3 Dialog / Drawer / Popover modals.
 *
 * Chakra occasionally leaves the body inert after a close animation —
 * `style="pointer-events: none; overflow: hidden"` plus `data-scroll-lock`
 * and `data-inert` attributes. The page then freezes: clicks land on the
 * body which still has `pointer-events: none`, and nothing happens.
 *
 * This hook runs `delayMs` (default 350) after the modal transitions to
 * closed and forcibly strips those styles if they're still on the body.
 * Harmless when Chakra cleaned up correctly (no-op), reliable when it
 * didn't.
 *
 * Pair every long-lived `<Dialog.Root open={...} />` with this hook by
 * passing the same `open` flag.
 */
export function useDialogBodyCleanup(open: boolean, delayMs = 350) {
    useEffect(() => {
        if (open) return
        const id = window.setTimeout(() => {
            if (document.body.style.pointerEvents === 'none') {
                document.body.style.pointerEvents = ''
                document.body.style.overflow = ''
                document.body.removeAttribute('data-scroll-lock')
                document.body.removeAttribute('data-inert')
            }
        }, delayMs)
        return () => window.clearTimeout(id)
    }, [open, delayMs])
}
